/**
 * Where the drawer's hover card sits next to an asset row. The card's height
 * depends on what it shows (a photo, or a summary that loads later), so the
 * vertical position is worked out from the card's measured height.
 */
export const PREVIEW_MAX_WIDTH = 240;
export const PREVIEW_GAP = 8;
export const PREVIEW_MARGIN = 8;
const DOWNLOAD_CLEARANCE = 44;
const MIN_WIDTH = 96;

type Edges = { top: number; bottom: number };
type Box = Edges & { left: number; right: number; width: number };

/** The right edge stops short of the row's download control. */
export const placePreviewHorizontally = (anchor: Box, bounds: Box) => {
  const minLeft = bounds.left + PREVIEW_MARGIN;
  const maxRight = Math.min(
    bounds.right - PREVIEW_MARGIN,
    anchor.right - DOWNLOAD_CLEARANCE
  );
  const available = maxRight - minLeft;
  const width =
    available >= MIN_WIDTH
      ? Math.min(PREVIEW_MAX_WIDTH, available)
      : Math.min(
          PREVIEW_MAX_WIDTH,
          Math.max(MIN_WIDTH, bounds.width - PREVIEW_MARGIN * 2)
        );
  const left = Math.min(
    Math.max(minLeft, anchor.left),
    Math.max(minLeft, bounds.right - PREVIEW_MARGIN - width)
  );
  return { left, width };
};

/**
 * The card's bottom edge sits `PREVIEW_GAP` above the row. It flips below the
 * row only when there isn't room above, and stays inside `bounds` either way.
 */
export const placePreviewVertically = (
  anchor: Edges,
  bounds: Edges,
  height: number
) => {
  const minTop = bounds.top + PREVIEW_MARGIN;
  const maxBottom = bounds.bottom - PREVIEW_MARGIN;
  const above = anchor.top - PREVIEW_GAP - height;
  if (above >= minTop) return above;
  const below = anchor.bottom + PREVIEW_GAP;
  if (below + height <= maxBottom) return below;
  return Math.max(minTop, Math.min(below, maxBottom - height));
};
